import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import * as Popover from '@radix-ui/react-popover';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { Button, EmptyState, ErrorState, Skeleton, useToast } from '@/components/ui';
import { describeError } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatRelative } from '@/lib/format';
import { useSession } from '@/lib/session';
import { markRead, notificationHref, notificationKeys, type InboxItem, type InboxPage } from '@/features/notifications/api';
import '@/features/notifications/notifications.css';

import { useNotificationFeed } from '@/features/notifications/useNotificationFeed';

export function NotificationsBell({ platform = false, navigateTo }: { platform?: boolean; navigateTo?: (to: string) => void }) {
  const { principal, impersonation } = useSession();

  const toast = useToast();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const query = useNotificationFeed(platform);
  const bellKey = query.queryKey;
  const context = JSON.stringify(bellKey);
  const previous = useRef<{ context: string; newest: string } | null>(null);
  useEffect(() => {
    if (!query.data) return;
    const newest = query.data.items.reduce((latest, item) => item.createdAt > latest ? item.createdAt : latest, '');
    const prior = previous.current;
    previous.current = { context, newest: prior?.context === context && prior.newest > newest ? prior.newest : newest };
    if (!prior || prior.context !== context || impersonation) return;
    const arrivals = query.data.items.filter(item => !item.readAt && item.createdAt > prior.newest);
    if (arrivals.length) toast.show({ tone: 'info', title: arrivals.length === 1 ? arrivals[0]!.title : `${arrivals.length} new notifications`, description: 'Open the notification bell to view updates.' });
  }, [query.data, context, impersonation, toast]);

  const mark = useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) => markRead(body, platform),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: bellKey });
      const prev = qc.getQueryData<InboxPage>(bellKey);
      if (prev) {
        const now = new Date().toISOString();
        const items = prev.items.map((n) => (body.all || body.ids?.includes(n.id) ? { ...n, readAt: n.readAt ?? now } : n));
        const newlyRead = prev.items.filter((n) => !n.readAt && (body.all || body.ids?.includes(n.id))).length;
        qc.setQueryData<InboxPage>(bellKey, { ...prev, items, unread: body.all ? 0 : Math.max(0, prev.unread - newlyRead) });
      }
      return { prev };
    },
    onError: (error, _v, ctx) => { if (ctx?.prev) qc.setQueryData(bellKey, ctx.prev); toast.show({ tone: 'danger', ...describeError(error) }); },
    // Refresh the bell and any open inbox page.
    onSettled: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });

  const unread = query.data?.unread ?? 0;
  const items = query.data?.items ?? [];

  const openItem = (n: InboxItem) => {
    if (!n.readAt && !impersonation) mark.mutate({ ids: [n.id] });
    setOpen(false);
    const href = notificationHref(n, principal?.kind === 'client' ? 'client' : 'staff');
    if (href) (navigateTo ?? navigate)(href);
  };

  return (
    <Popover.Root open={open} onOpenChange={value => { setOpen(value); if (value) void query.refetch(); }}>
      <Popover.Trigger asChild>
        <button type="button" className="btn btn-ghost btn-icon-only btn-md notif-trigger" aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}>
          <Bell size={18} aria-hidden />
          {unread > 0 && (
            <span className="notif-dot tabular" aria-hidden>
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="popover notif-pop" align="end" sideOffset={6} collisionPadding={8}>
          <div className="notif-head">
            <h2>Notifications</h2>
            <Button size="sm" variant="ghost" icon={<CheckCheck size={14} />} disabled={!unread || !!impersonation || mark.isPending} onClick={() => mark.mutate({ all: true })}>
              Mark all read
            </Button>
          </div>
          <div className="notif-list">
            {query.isPending &&
              Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="notif-item" aria-hidden>
                  <span />
                  <span className="stack" style={{ ['--stack-gap' as string]: '6px' }}>
                    <Skeleton width="70%" />
                    <Skeleton width="45%" height={10} />
                  </span>
                </div>
              ))}
            {query.isError && <ErrorState size="sm" error={query.error} onRetry={() => void query.refetch()} />}
            {query.data && items.length === 0 && <EmptyState size="sm" icon={<Bell size={16} />} title="You’re all caught up" description="Project updates, tasks, announcements and review requests will appear here." />}
            {items.map((n) => (
              <button key={n.id} type="button" className={cx('notif-item', n.readAt && 'is-read')} onClick={() => openItem(n)}>
                <span className="notif-unread-dot" aria-hidden />
                <span>
                  <span className="notif-title">
                    {!n.readAt && <span className="sr-only">Unread: </span>}
                    {n.title}
                  </span>
                  {n.body && <p className="notif-body">{n.body}</p>}
                  <p className="notif-time">{n.tenantName && `${n.tenantName} ? ${n.recipientName} ? `}{formatRelative(n.createdAt)}</p>
                </span>
              </button>
            ))}
          </div>
          <div className="notif-foot">
            <Link to={platform ? '/platform/notifications' : principal?.kind === 'client' ? '/portal/notifications' : '/notifications'} className="notif-view-all" onClick={event => { setOpen(false); if (navigateTo) { event.preventDefault(); navigateTo(platform ? '/platform/notifications' : principal?.kind === 'client' ? '/portal/notifications' : '/notifications'); } }}>
              View all notifications
            </Link>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
